(() => {
  const IMG_CACHE = new Map();
  const GALLERY_ORIG = new WeakMap();

  // Stock map is populated from the JSON embedded in Liquid
  let STOCK_MAP = null;

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const parsePackSize = (value) => {
    if (!value) return 0;
    let v = String(value).trim();
    if (!v) return 0;

    // Common label formats: "3 Pack", "3-Pack", "3", "3x".
    v = v.split(' ')[0];
    if (v.includes('-')) v = v.split('-')[0];
    if (v.toLowerCase().endsWith('x')) v = v.slice(0, -1);
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };

  const getAllSlots = (root) => $$('[data-bundle-slot]', root);
  const getActiveSlots = (root) => getAllSlots(root).filter((el) => !el.hasAttribute('hidden') && !el.classList.contains('bundle-slot--hidden'));

  const clearSlotSelection = (slotEl) => {
    $$('[data-bundle-swatch].is-selected', slotEl).forEach((btn) => {
      btn.classList.remove('is-selected');
      btn.setAttribute('aria-pressed', 'false');
    });
    const input = $('input[type="hidden"][data-bundle-selected-handle]', slotEl);
    if (input) input.value = '';
  };

  const clearAllSelections = (root) => {
    getAllSlots(root).forEach(clearSlotSelection);
    updateAllSlotLabels(root);
  };

  function updateSlotLabel(slot) {
    const labelEl = $("[data-bundle-slot-label]", slot);
    if (!labelEl) return;

    const selected = $("[data-bundle-swatch].is-selected", slot);
    const sr = selected ? $(".sr-only", selected) : null;
    const selectedLabel = ((selected && selected.getAttribute("aria-label")) || (sr ? sr.textContent : "") || "").trim();

    const fallback = labelEl.getAttribute("data-default-label") || labelEl.textContent || "";
    labelEl.textContent = selectedLabel || fallback;
  }

  function updateAllSlotLabels(root) {
    $$("[data-bundle-slot]", root).forEach(updateSlotLabel);
  }

  function getRoot() {
    return $('[data-bundle-color-pickers]');
  }

  function getFormId(root) {
    return root?.getAttribute('data-form-id') || null;
  }

  /**
   * Get the selected value for an option by position (1-indexed).
   * Handles radio buttons, hidden inputs, and selects.
   * Also checks for theme's x-listbox / popover hidden inputs.
   */
  function getSelectedOption(formId, optionPosition) {
    if (!formId) return null;
    
    const optionName = `option${optionPosition}`;
    
    // Strategy 1: Check radio buttons with form attribute
    const radio = $(`input[name="${optionName}"][form="${formId}"]:checked`);
    if (radio?.value) return radio.value;

    // Strategy 2: Check select elements with form attribute
    const select = $(`select[name="${optionName}"][form="${formId}"]`);
    if (select?.value) return select.value;

    // Strategy 3: Check hidden inputs with form attribute (used by dropdowns/popovers)
    const hidden = $(`input[name="${optionName}"][type="hidden"][form="${formId}"]`);
    if (hidden?.value) return hidden.value;

    // Strategy 4: Look inside the actual form element
    const formEl = document.getElementById(formId);
    if (formEl) {
      const radioInForm = $(`input[name="${optionName}"]:checked`, formEl);
      if (radioInForm?.value) return radioInForm.value;

      const selectInForm = $(`select[name="${optionName}"]`, formEl);
      if (selectInForm?.value) return selectInForm.value;

      const hiddenInForm = $(`input[name="${optionName}"][type="hidden"]`, formEl);
      if (hiddenInForm?.value) return hiddenInForm.value;
    }

    // Strategy 5: Check variant-picker component for this form
    const variantPicker = $(`variant-picker[form="${formId}"]`);
    if (variantPicker) {
      const radioInPicker = $(`input[name="${optionName}"]:checked`, variantPicker);
      if (radioInPicker?.value) return radioInPicker.value;

      const hiddenInPicker = $(`input[name="${optionName}"][type="hidden"]`, variantPicker);
      if (hiddenInPicker?.value) return hiddenInPicker.value;
    }

    // Strategy 6: Global fallback - find any input with this name that's checked/has value
    const anyRadio = $(`input[name="${optionName}"]:checked`);
    if (anyRadio?.value) return anyRadio.value;

    const anyHidden = $(`input[name="${optionName}"][type="hidden"]`);
    if (anyHidden?.value) return anyHidden.value;

    return null;
  }

  // Pack Size is option1 (first option on product)
  function getSelectedPackSize(formId) {
    const value = getSelectedOption(formId, 1);
    return parsePackSize(value);
  }

  // Size is option2 (second option on product)
  function getSelectedSize(formId) {
    return getSelectedOption(formId, 2);
  }

  /**
   * Load the stock map from the embedded JSON in Liquid.
   * This is called once on init and provides instant stock lookups.
   */
  function loadStockMap() {
    if (STOCK_MAP) return STOCK_MAP;

    const script = $('script[data-bundle-stock-map]');
    if (!script) {
      console.warn('[bundle-builder] No stock map found. Falling back to always-available.');
      STOCK_MAP = {};
      return STOCK_MAP;
    }

    try {
      STOCK_MAP = JSON.parse(script.textContent);
    } catch (e) {
      console.error('[bundle-builder] Failed to parse stock map:', e);
      STOCK_MAP = {};
    }

    return STOCK_MAP;
  }

  /**
   * Get stock info for a handle + size from the embedded map.
   * Returns { available: boolean, variantId: number } or null if not found.
   */
  /**
   * Detect if this is a one-size product (no option2 / size selector on page).
   * Hat bundles only have Pack Size (option1), no Size (option2).
   */
  function isOneSizeProduct() {
    return !document.querySelector('input[name="option2"], select[name="option2"]');
  }

  function getOneSizeKey() {
    const map = loadStockMap();
    const firstProduct = Object.values(map)[0];
    if (!firstProduct || !firstProduct.sizes) return null;
    const keys = Object.keys(firstProduct.sizes);
    return keys.length >= 1 ? keys[0] : null;
  }


  function getStockInfo(handle, size) {
    const map = loadStockMap();
    const product = map[handle];
    if (!product || !product.sizes) return null;

    // Try exact match first
    if (product.sizes[size]) {
      return product.sizes[size];
    }

    // Try case-insensitive match
    const sizeNorm = String(size).trim().toLowerCase();
    for (const [key, val] of Object.entries(product.sizes)) {
      if (String(key).trim().toLowerCase() === sizeNorm) {
        return val;
      }
    }

    return null;
  }

  /**
   * Get the product image URL from the embedded map.
   */
  function getProductImage(handle) {
    const map = loadStockMap();
    const product = map[handle];
    return product?.image || null;
  }

  /**
   * Preload an image URL and return the fully-loaded Image object.
   * Uses img.decode() when available for smoother rendering.
   */
  function preloadImage(url) {
    if (!url) return Promise.resolve(null);
    if (IMG_CACHE.has(url)) return IMG_CACHE.get(url);

    const p = new Promise((resolve) => {
      const img = new Image();

      img.onload = async () => {
        // Use decode() if available for smoother swap
        if (typeof img.decode === 'function') {
          try {
            await img.decode();
          } catch (e) {
            // decode() can fail on some images, continue anyway
          }
        }
        resolve(img);
      };

      img.onerror = () => resolve(null);
      img.src = url;
    });

    IMG_CACHE.set(url, p);
    return p;
  }

  function addWidthParam(url, width) {
    if (!url) return url;
    const w = Number(width || 1200);
    if (!w) return url;
    // If URL already has width param, replace it
    if (url.includes('width=')) {
      return url.replace(/width=\d+/, `width=${w}`);
    }
    return url.includes('?') ? `${url}&width=${w}` : `${url}?width=${w}`;
  }

  function getGallery(root) {
    const section = root.closest('.shopify-section') || document;
    
    // First, check for the new bundle-gallery component
    const bundleGallery = $('[data-bundle-gallery]', section) || $('[data-bundle-gallery]');
    if (bundleGallery) return bundleGallery;
    
    // Fall back to the standard product-gallery
    return $('product-gallery', section) || $('product-gallery');
  }

  /**
   * Check if we're using the new bundle-gallery component
   */
  function isBundleGallery(gallery) {
    return gallery && gallery.hasAttribute('data-bundle-gallery');
  }

  function snapshotGallery(gallery) {
    if (!gallery || GALLERY_ORIG.has(gallery)) return;

    const mediaEls = $$('.product-gallery__media', gallery);
    const thumbEls = $$('.product-gallery__thumbnail', gallery);

    const mediaImgs = mediaEls.map(el => $('img', el));
    const thumbImgs = thumbEls.map(el => $('img', el));

    const mediaState = mediaEls.map((el, i) => {
      const img = mediaImgs[i];
      return {
        hidden: el.hidden,
        innerHTML: el.innerHTML, // Capture original HTML for placeholder restoration
        src: img ? img.getAttribute('src') : null,
        srcset: img ? img.getAttribute('srcset') : null,
        sizes: img ? img.getAttribute('sizes') : null,
      };
    });

    const thumbState = thumbEls.map((el, i) => {
      const img = thumbImgs[i];
      return {
        hidden: el.hidden,
        ariaCurrent: el.getAttribute('aria-current'),
        src: img ? img.getAttribute('src') : null,
        srcset: img ? img.getAttribute('srcset') : null,
        sizes: img ? img.getAttribute('sizes') : null,
      };
    });

    // Find the containers for media and thumbnails
    const carousel = $('.product-gallery__carousel, .scroll-carousel, scroll-carousel', gallery);
    const thumbContainer = $('.product-gallery__thumbnail-list, product-gallery-navigation, .product-gallery__thumbnail-scroller', gallery);

    GALLERY_ORIG.set(gallery, {
      mediaEls,
      thumbEls,
      mediaImgs,
      thumbImgs,
      mediaState,
      thumbState,
      carousel,
      thumbContainer,
      // Track cloned elements so we can remove them on restore
      clonedMediaEls: [],
      clonedThumbEls: [],
    });
  }

  function restoreGallery(gallery) {
    const orig = gallery ? GALLERY_ORIG.get(gallery) : null;
    if (!orig) return;

    // Remove any cloned elements first
    orig.clonedMediaEls.forEach(el => el.remove());
    orig.clonedThumbEls.forEach(el => el.remove());
    orig.clonedMediaEls = [];
    orig.clonedThumbEls = [];

    // Restore original media elements
    orig.mediaEls.forEach((el, i) => {
      const st = orig.mediaState[i];
      if (!st) return;
      
      el.hidden = !!st.hidden;
      el.removeAttribute('data-bundle-placeholder');
      
      // Restore original innerHTML if it was replaced with placeholder
      if (st.innerHTML && el.querySelector('.bundle-gallery-placeholder')) {
        el.innerHTML = st.innerHTML;
      }
      
      // Re-query for img since innerHTML might have been restored
      const img = $('img', el);
      if (img) {
        if (st.src != null) img.setAttribute('src', st.src);
        if (st.srcset != null) img.setAttribute('srcset', st.srcset);
        else img.removeAttribute('srcset');
        if (st.sizes != null) img.setAttribute('sizes', st.sizes);
        else img.removeAttribute('sizes');
        img.classList.add('is-loaded');
      }
    });

    // Update the mediaImgs array since we may have restored innerHTML
    orig.mediaImgs = orig.mediaEls.map(el => $('img', el));

    // Restore original thumbnail elements
    orig.thumbEls.forEach((el, i) => {
      el.hidden = !!orig.thumbState[i]?.hidden;
      const st = orig.thumbState[i];
      if (st) {
        if (st.ariaCurrent == null) el.removeAttribute('aria-current');
        else el.setAttribute('aria-current', st.ariaCurrent);
      }
      const img = orig.thumbImgs[i];
      if (!img || !st) return;
      if (st.src != null) img.setAttribute('src', st.src);
      if (st.srcset != null) img.setAttribute('srcset', st.srcset);
      else img.removeAttribute('srcset');
      if (st.sizes != null) img.setAttribute('sizes', st.sizes);
      else img.removeAttribute('sizes');
      img.classList.add('is-loaded');
    });
  }

  /**
   * Clone a media element for additional gallery slots
   */
  function cloneMediaElement(templateEl, index) {
    const clone = templateEl.cloneNode(true);
    clone.hidden = false;
    clone.classList.remove('is-initial', 'active');
    clone.setAttribute('data-bundle-cloned', 'true');
    clone.setAttribute('aria-label', `Item ${index + 1}`);
    
    // Clear any media IDs to avoid conflicts
    clone.removeAttribute('data-media-id');
    
    // Get the image and clear its src (will be set later)
    const img = $('img', clone);
    if (img) {
      img.removeAttribute('src');
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.classList.remove('is-loaded');
    }
    
    return clone;
  }

  /**
   * Clone a thumbnail element for additional gallery slots
   */
  function cloneThumbnailElement(templateEl, index) {
    const clone = templateEl.cloneNode(true);
    clone.hidden = false;
    clone.setAttribute('data-bundle-cloned', 'true');
    clone.setAttribute('aria-current', 'false');
    clone.setAttribute('aria-label', `Go to item ${index + 1}`);
    
    // Clear any media IDs to avoid conflicts
    clone.removeAttribute('data-media-id');
    clone.removeAttribute('data-media-position');
    
    // Get the image and clear its src (will be set later)
    const img = $('img', clone);
    if (img) {
      img.removeAttribute('src');
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.classList.remove('is-loaded');
    }
    
    return clone;
  }

  /**
   * Swap an image src only after it's fully loaded + decoded.
   * This prevents the flash/pop-in by ensuring the browser has the
   * image ready to paint before we update the DOM.
   */
  async function swapImageSmooth(imgEl, newUrl) {
    if (!imgEl || !newUrl) return;

    // Remove loaded class to prepare for potential transition
    imgEl.classList.remove('is-loaded');

    // Preload and decode the new image
    const preloaded = await preloadImage(newUrl);

    // Now swap - browser should paint immediately since image is cached + decoded
    imgEl.setAttribute('src', newUrl);
    imgEl.removeAttribute('srcset');
    imgEl.removeAttribute('sizes');

    // If preload succeeded and decode is available, decode the actual element
    if (preloaded && typeof imgEl.decode === 'function') {
      try {
        await imgEl.decode();
      } catch (e) {
        // Ignore decode errors
      }
    }

    // Add loaded class for CSS transition
    imgEl.classList.add('is-loaded');
  }

  /**
   * Get the ordinal suffix for a number (1st, 2nd, 3rd, etc.)
   */
  function getOrdinal(n) {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  async function updateBundleGallery(root, clickedSlotIndex = null, options = {}) {
    const gallery = getGallery(root);
    if (!gallery) return;

    // Use the new simple bundle-gallery component
    if (isBundleGallery(gallery)) {
      await updateBundleGallerySimple(root, gallery, clickedSlotIndex, options);
      return;
    }

    // Fall back to old product-gallery logic (for backwards compatibility)
    await updateProductGallery(root, gallery);
  }

  /**
   * Update the new bundle-gallery component (simple CSS grid with pre-rendered slots)
   * 
   * Behavior:
   * - No selections: Show shell product images (initial state from Liquid)
   * - Some selections: Show selected color images in order, then ONE placeholder for next slot
   * - All selections: Show all color images, no placeholder
   * 
   * @param {number|null} clickedSlotIndex - The slot that was just clicked (1-based), for scroll targeting
   * @param {object} options - { skipScroll: boolean }
   */
  async function updateBundleGallerySimple(root, gallery, clickedSlotIndex = null, options = {}) {
    const { skipScroll = false } = options;
    
    const isMobile = window.innerWidth < 1000;
    
    // ── Scroll-anchor fix (mobile) ──────────────────────────────────
    // On mobile, the gallery sits ABOVE product-info in the CSS grid.
    // When gallery slots hide/show, the grid row height changes and
    // pushes product-info up or down — visible as a "page jump."
    //
    // Fix: record the viewport-relative top of product-info before any
    // DOM changes, then after changes, adjust window.scrollY so that
    // product-info stays in the same visual position.
    let scrollAnchor = null;
    if (isMobile) {
      const anchorEl = $('[data-bundle-color-pickers]') || $('.product-info');
      if (anchorEl) {
        scrollAnchor = {
          el: anchorEl,
          viewportTop: anchorEl.getBoundingClientRect().top,
        };
      }
    }
    
    // Lock gallery height as secondary guard against mid-frame flicker
    const galleryRect = gallery.getBoundingClientRect();
    gallery.style.minHeight = galleryRect.height + 'px';

    // Get the required number of slots (from pack size)
    const requiredSlots = parseInt(root.dataset.requiredSlots || '0', 10) || getActiveSlots(root).length;
    
    // Get selections in slot order (includes nulls for empty slots)
    const slotHandles = getSelections(root);
    
    // Count how many are actually selected
    const selectedCount = slotHandles.filter(Boolean).length;
    
    // Get all slot elements in the gallery
    const slotEls = $$('[data-bundle-gallery-slot]', gallery);
    
    console.log('[bundle-builder] Gallery update:', { 
      requiredSlots, 
      slotHandles, 
      selectedCount,
      slotCount: slotEls.length 
    });

    // No selections: restore shell product images (initial state)
    if (!selectedCount) {
      restoreBundleGallery(gallery, slotEls);
      releaseGalleryHeight(gallery, scrollAnchor);
      return;
    }

    // Build display list: selected images in order, then ONE placeholder for next empty slot
    const displayItems = [];
    
    // Add selected colors in order
    for (let i = 0; i < slotHandles.length; i++) {
      const handle = slotHandles[i];
      if (handle) {
        displayItems.push({ type: 'image', handle, slotIndex: displayItems.length + 1 });
      }
    }
    
    // Add ONE placeholder for the next slot (if not all filled)
    if (selectedCount < requiredSlots) {
      displayItems.push({ type: 'placeholder', slotIndex: displayItems.length + 1 });
    }

    // Ensure we have enough slot elements
    while (slotEls.length < displayItems.length) {
      const newSlot = createGallerySlot(gallery, slotEls.length + 1);
      gallery.appendChild(newSlot);
      slotEls.push(newSlot);
    }

    // Preload images first
    const preloadTasks = displayItems
      .filter(item => item.type === 'image')
      .map(item => {
        const imageUrl = getProductImage(item.handle);
        return imageUrl ? preloadImage(addWidthParam(imageUrl, 800)) : Promise.resolve();
      });
    
    await Promise.allSettled(preloadTasks);

    // Track slots for scrolling
    let lastImageSlot = null;
    let scrollTargetSlot = null;

    // Update each slot
    for (let i = 0; i < slotEls.length; i++) {
      const slotEl = slotEls[i];
      const item = displayItems[i];
      
      if (!item) {
        // Hide extra slots
        slotEl.hidden = true;
        continue;
      }
      
      slotEl.hidden = false;
      
      if (item.type === 'image') {
        const imageUrl = getProductImage(item.handle);
        if (imageUrl) {
          await showSlotImage(slotEl, addWidthParam(imageUrl, 800), item.slotIndex);
          lastImageSlot = slotEl;
          
          // Track if this is the slot that was just clicked
          if (clickedSlotIndex && item.slotIndex === clickedSlotIndex) {
            scrollTargetSlot = slotEl;
          }
        }
      } else if (item.type === 'placeholder') {
        showSlotPlaceholder(slotEl, item.slotIndex);
      }
    }

    // Release height lock and restore scroll position
    releaseGalleryHeight(gallery, scrollAnchor);

    // On desktop only, scroll to the image that was just changed
    if (!skipScroll) {
      const isDesktop = window.matchMedia('(min-width: 1000px)').matches;
      const targetSlot = scrollTargetSlot || lastImageSlot;
      if (isDesktop && targetSlot) {
        requestAnimationFrame(() => {
          targetSlot.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
    }
  }

  /**
   * Release the gallery minHeight lock and correct scroll position on mobile
   * so that the element the user is interacting with doesn't visually shift.
   */
  function releaseGalleryHeight(gallery, scrollAnchor) {
    // Release minHeight so the grid can settle to its natural size
    gallery.style.minHeight = '';

    // Correct scroll drift on mobile
    if (scrollAnchor && scrollAnchor.el) {
      // Force layout so getBoundingClientRect reflects the new height
      const viewportTopAfter = scrollAnchor.el.getBoundingClientRect().top;
      const drift = viewportTopAfter - scrollAnchor.viewportTop;

      if (Math.abs(drift) > 1) {
        // Instant scroll adjustment — no smooth behavior, user shouldn't perceive it
        window.scrollBy({ top: drift, left: 0, behavior: 'instant' });
      }
    }
  }

  /**
   * Restore the bundle gallery to show shell product images (initial state)
   * Shows ALL shell product images - no limiting by pack size
   */
  function restoreBundleGallery(gallery, slotEls) {
    slotEls.forEach((slotEl, i) => {
      slotEl.removeAttribute('data-bundle-placeholder');
      
      // Hide placeholder
      const placeholder = $('.bundle-gallery__placeholder', slotEl);
      if (placeholder) {
        placeholder.style.display = 'none';
      }
      
      // Restore original image if it has one
      const img = $('img', slotEl);
      if (img) {
        const originalSrc = img.getAttribute('data-original-src');
        if (originalSrc) {
          img.src = originalSrc;
          img.style.display = '';
          img.classList.add('is-loaded');
          slotEl.hidden = false;
        } else {
          // No original image - hide this slot entirely
          slotEl.hidden = true;
        }
      } else {
        slotEl.hidden = true;
      }
    });
  }

  /**
   * Create a new gallery slot element
   */
  function createGallerySlot(gallery, slotIndex) {
    const slot = document.createElement('div');
    slot.className = 'bundle-gallery__media';
    slot.setAttribute('data-bundle-gallery-slot', slotIndex);
    slot.setAttribute('role', 'group');
    slot.setAttribute('aria-label', `Slot ${slotIndex}`);
    
    // Add placeholder content
    showSlotPlaceholder(slot, slotIndex);
    
    // Add hidden img for later use
    const img = document.createElement('img');
    img.src = '';
    img.alt = '';
    img.loading = 'lazy';
    img.style.display = 'none';
    img.style.position = 'absolute';
    img.style.inset = '0';
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'cover';
    slot.appendChild(img);
    
    return slot;
  }

  /**
   * Show placeholder in a gallery slot
   */
  function showSlotPlaceholder(slotEl, slotIndex) {
    slotEl.setAttribute('data-bundle-placeholder', 'true');
    
    // Hide image if present
    const img = $('img', slotEl);
    if (img) {
      img.style.display = 'none';
      img.classList.remove('is-loaded');
    }
    
    // Show placeholder content
    let placeholder = $('.bundle-gallery__placeholder', slotEl);
    if (!placeholder) {
      // Create placeholder
      placeholder = document.createElement('div');
      placeholder.className = 'bundle-gallery__placeholder';
      placeholder.innerHTML = `
        <div class="bundle-gallery__placeholder-icon">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
        </div>
        <span class="bundle-gallery__placeholder-text">Add your ${getOrdinal(slotIndex)} item</span>
      `;
      slotEl.insertBefore(placeholder, slotEl.firstChild);
    } else {
      placeholder.style.display = '';
      // Update text
      const textEl = $('.bundle-gallery__placeholder-text', placeholder);
      if (textEl) {
        textEl.textContent = `Add your ${getOrdinal(slotIndex)} item`;
      }
    }
  }

  /**
   * Show image in a gallery slot
   */
  async function showSlotImage(slotEl, imageUrl, slotIndex) {
    slotEl.removeAttribute('data-bundle-placeholder');
    
    // Hide placeholder
    const placeholder = $('.bundle-gallery__placeholder', slotEl);
    if (placeholder) {
      placeholder.style.display = 'none';
    }
    
    // Show and update image
    let img = $('img', slotEl);
    if (!img) {
      img = document.createElement('img');
      img.loading = 'lazy';
      slotEl.appendChild(img);
    }
    
    img.style.display = '';
    img.classList.remove('is-loaded');
    
    // Preload and swap
    await preloadImage(imageUrl);
    img.src = imageUrl;
    img.alt = `Bundle item ${slotIndex}`;
    
    if (typeof img.decode === 'function') {
      try {
        await img.decode();
      } catch (e) {}
    }
    
    img.classList.add('is-loaded');
  }

  /**
   * Legacy: Update the old product-gallery component
   */
  async function updateProductGallery(root, gallery) {
    snapshotGallery(gallery);

    const orig = GALLERY_ORIG.get(gallery);
    if (!orig) return;

    // Get the required number of slots (from pack size)
    const requiredSlots = parseInt(root.dataset.requiredSlots || '0', 10) || getActiveSlots(root).length;
    
    // Get selections in slot order (includes nulls for empty slots)
    const slotHandles = getSelections(root);
    
    // Count how many are actually selected
    const selectedCount = slotHandles.filter(Boolean).length;

    // No selections: show shell product gallery as-is
    if (!selectedCount) {
      restoreGallery(gallery);
      return;
    }

    const originalMediaCount = orig.mediaEls.length;
    const originalThumbCount = orig.thumbEls.length;

    // Remove old clones and placeholders first
    orig.clonedMediaEls.forEach(el => el.remove());
    orig.clonedThumbEls.forEach(el => el.remove());
    orig.clonedMediaEls = [];
    orig.clonedThumbEls = [];

    // Build display items: images first, then one placeholder
    const displayItems = [];
    
    for (let i = 0; i < slotHandles.length; i++) {
      const handle = slotHandles[i];
      if (handle) {
        displayItems.push({ type: 'image', handle, slotIndex: i + 1 });
      }
    }
    
    // Add one placeholder for the next empty slot
    if (selectedCount < requiredSlots) {
      for (let i = 0; i < requiredSlots; i++) {
        if (!slotHandles[i]) {
          displayItems.push({ type: 'placeholder', slotIndex: i + 1 });
          break;
        }
      }
    }

    const neededCount = displayItems.length;

    // Build arrays of all media/thumb elements we'll use (original + clones)
    let allMediaEls = [...orig.mediaEls];
    let allMediaImgs = [...orig.mediaImgs];
    let allThumbEls = [...orig.thumbEls];
    let allThumbImgs = [...orig.thumbImgs];

    // Clone additional media elements if needed
    if (neededCount > originalMediaCount && orig.carousel && orig.mediaEls[0]) {
      const template = orig.mediaEls[0];
      for (let i = originalMediaCount; i < neededCount; i++) {
        const clone = cloneMediaElement(template, i);
        orig.carousel.appendChild(clone);
        orig.clonedMediaEls.push(clone);
        allMediaEls.push(clone);
        allMediaImgs.push($('img', clone));
      }
    }

    // Clone additional thumbnail elements if needed
    if (neededCount > originalThumbCount && orig.thumbContainer && orig.thumbEls[0]) {
      const template = orig.thumbEls[0];
      for (let i = originalThumbCount; i < neededCount; i++) {
        const clone = cloneThumbnailElement(template, i);
        orig.thumbContainer.appendChild(clone);
        orig.clonedThumbEls.push(clone);
        allThumbEls.push(clone);
        allThumbImgs.push($('img', clone));
      }
    }

    const maxMedia = Math.min(neededCount, allMediaEls.length);

    // Hide all media elements first
    orig.mediaEls.forEach(el => { el.hidden = true; });
    orig.thumbEls.forEach(el => { el.hidden = true; });

    // Preload images
    const preloadTasks = [];
    for (let i = 0; i < maxMedia; i++) {
      const item = displayItems[i];
      if (item?.type === 'image') {
        const baseUrl = getProductImage(item.handle);
        if (baseUrl) {
          preloadTasks.push(preloadImage(addWidthParam(baseUrl, 1400)));
        }
      }
    }
    await Promise.allSettled(preloadTasks);

    // Update each slot
    for (let i = 0; i < maxMedia; i++) {
      const item = displayItems[i];
      const mediaEl = allMediaEls[i];
      const thumbEl = allThumbEls[i];
      let mediaImg = allMediaImgs[i];
      const thumbImg = allThumbImgs[i];

      if (!item || !mediaEl) continue;

      mediaEl.hidden = false;

      if (item.type === 'image') {
        // Restore original HTML if was a placeholder
        if (mediaEl.hasAttribute('data-bundle-placeholder')) {
          const originalIndex = orig.mediaEls.indexOf(mediaEl);
          if (originalIndex !== -1 && orig.mediaState[originalIndex]?.innerHTML) {
            mediaEl.innerHTML = orig.mediaState[originalIndex].innerHTML;
            mediaImg = $('img', mediaEl);
            allMediaImgs[i] = mediaImg;
          }
        }
        mediaEl.removeAttribute('data-bundle-placeholder');

        const baseUrl = getProductImage(item.handle);
        if (mediaImg && baseUrl) {
          await swapImageSmooth(mediaImg, addWidthParam(baseUrl, 1400));
        }
        if (thumbEl) {
          thumbEl.hidden = false;
          if (thumbImg && baseUrl) {
            await swapImageSmooth(thumbImg, addWidthParam(baseUrl, 300));
          }
        }
      } else if (item.type === 'placeholder') {
        mediaEl.setAttribute('data-bundle-placeholder', 'true');
        const placeholderTemplate = $('template[data-bundle-placeholder-template]');
        if (placeholderTemplate) {
          mediaEl.innerHTML = '';
          const content = placeholderTemplate.content.cloneNode(true);
          const textEl = $('[data-bundle-placeholder-text]', content);
          if (textEl) {
            textEl.textContent = `Add your ${getOrdinal(item.slotIndex)} item`;
          }
          mediaEl.appendChild(content);
        }
        if (thumbEl) thumbEl.hidden = true;
      }
    }
  }

  function setSwatchUI(btn, { selected, soldOut }) {
    const isSelected = !!selected;
    const isSoldOut = !!soldOut;

    // Selection UI
    btn.classList.toggle('is-selected', isSelected);
    btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

    // Clear any theme-level soldout classes that can cause pseudo-element weirdness
    btn.classList.remove('sold_out');
    btn.classList.remove('is-disabled');

    // Our own soldout state (used for styling and logic)
    btn.dataset.bundleSoldout = isSoldOut ? 'true' : 'false';
    btn.classList.toggle('bundle-swatch--soldout', isSoldOut);

    // Actual interactivity
    btn.disabled = isSoldOut;
    btn.setAttribute('aria-disabled', isSoldOut ? 'true' : 'false');
    btn.tabIndex = isSoldOut ? -1 : 0;
  }

  function clampSlots(n, min, max) {
    const num = parseInt(n, 10);
    if (!Number.isFinite(num)) return min;
    return Math.max(min, Math.min(max, num));
  }

  function getMaxSlots(root) {
    const max = parseInt(root.dataset.maxSlots || '12', 10);
    return Number.isFinite(max) && max > 0 ? max : 12;
  }

  function getPackSizeFromVariant(variant) {
    // Convention: option2 is pack size (option1 is Size)
    return parsePackSize(variant?.options?.[1] || variant?.option2 || '');
  }

  function applyRequiredSlots(root, requiredSlots, { resetSelections = false } = {}) {
    const maxSlots = getMaxSlots(root);
    const next = clampSlots(requiredSlots || 0, 1, maxSlots);
    const prev = parseInt(root.dataset.requiredSlots || '0', 10) || 0;

    console.log('[bundle-builder] applyRequiredSlots:', { requiredSlots, next, prev, maxSlots, resetSelections });

    root.dataset.requiredSlots = String(next);

    // Don't clear all selections - only clear slots that are being hidden
    // This preserves selections when going from 3-pack to 4-pack, etc.

    getAllSlots(root).forEach(slot => {
      const idx = parseInt(slot.getAttribute('data-bundle-slot') || '0', 10) || 0;
      const shouldHide = idx > next;

      slot.toggleAttribute('hidden', shouldHide);
      slot.classList.toggle('bundle-slot--hidden', shouldHide);

      // Only clear selection for slots being hidden (going down in pack size)
      if (shouldHide) {
        clearSlotSelection(slot);
      }
    });

    updateATCEnabled(root);
    updateBundleGallery(root, null, { skipScroll: true }); // Don't scroll on pack size change
  }

  function getSelections(root) {
    return getActiveSlots(root).map(slot => {
      const sel = $('[data-bundle-swatch].is-selected', slot);
      return sel ? sel.getAttribute('data-handle') : null;
    });
  }

  function updateATCEnabled(root) {
    const atc = $('[data-bundle-atc]');
    if (!atc) return;

    const formId = getFormId(root);
    let size = getSelectedSize(formId);
    if (!size && isOneSizeProduct()) size = getOneSizeKey();
    const picks = getSelections(root);

    const ready = !!size && picks.every(Boolean);
    atc.disabled = !ready;
    atc.setAttribute('aria-disabled', ready ? 'false' : 'true');
  }

  /**
   * Refresh sold-out state for all swatches based on selected size.
   * Now uses embedded stock map - no async fetches needed!
   */
  function refreshSoldOut(root, options = {}) {
    const { skipScroll = false } = options;
    const formId = getFormId(root);
    let size = getSelectedSize(formId);
    if (!size && isOneSizeProduct()) size = getOneSizeKey();
    if (!size) {
      updateATCEnabled(root);
      return;
    }

    const slots = getActiveSlots(root);
    for (const slot of slots) {
      const swatches = $$('[data-bundle-swatch]', slot);

      // Update sold-out states synchronously from embedded map
      for (const btn of swatches) {
        const handle = btn.getAttribute('data-handle');
        const stockInfo = getStockInfo(handle, size);
        const soldOut = !stockInfo || !stockInfo.available;
        setSwatchUI(btn, { selected: btn.classList.contains('is-selected'), soldOut });
      }

      // If the selected swatch becomes unavailable for this size, clear it.
      const current = $('[data-bundle-swatch].is-selected', slot);
      if (current && current.disabled) {
        current.classList.remove('is-selected');
        current.setAttribute('aria-pressed', 'false');
      }

      // Normalize selection UI
      for (const btn of swatches) {
        setSwatchUI(btn, { selected: btn.classList.contains('is-selected'), soldOut: btn.disabled });
      }

      updateSlotLabel(slot);
    }

    updateATCEnabled(root);
    updateBundleGallery(root, null, { skipScroll });
  }

  /**
   * Handle pack size change - called when option1 changes
   */
  function handlePackSizeChange(root, formId) {
    const packSize = getSelectedPackSize(formId);
    console.log('[bundle-builder] Pack size changed to:', packSize);
    
    if (packSize > 0) {
      applyRequiredSlots(root, packSize, { resetSelections: true });
    }
    refreshSoldOut(root, { skipScroll: true });
  }

  function bind(root) {
    // Load the stock map from embedded JSON
    loadStockMap();

    const formId = getFormId(root);
    console.log('[bundle-builder] Initializing with formId:', formId);

    // Force default state: no preselected colors
    $$('[data-bundle-swatch].is-selected', root).forEach((b) => {
      b.classList.remove('is-selected');
      b.setAttribute('aria-pressed', 'false');
    });
    updateAllSlotLabels(root);
    updateATCEnabled(root);

    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-bundle-swatch]');
      if (!btn || btn.disabled) return;

      const slot = btn.closest('[data-bundle-slot]');
      if (!slot) return;

      // Track which slot was clicked for scroll targeting
      const clickedSlotIndex = parseInt(slot.getAttribute('data-bundle-slot') || '0', 10);

      $$('[data-bundle-swatch]', slot).forEach(b => b.classList.remove('is-selected'));
      btn.classList.add('is-selected');
      updateSlotLabel(slot);

      $$('[data-bundle-swatch]', slot).forEach(b => {
        setSwatchUI(b, { selected: b.classList.contains('is-selected'), soldOut: b.disabled });
      });

      updateATCEnabled(root);

      // Skip scroll on mobile to prevent page jump
      const isMobile = window.innerWidth < 1000;
      updateBundleGallery(root, clickedSlotIndex, { skipScroll: isMobile });
    });

    if (formId) {
      // Listen on document for change events (inputs may be outside form with form="..." attribute)
      document.addEventListener('change', (e) => {
        const t = e.target;
        if (!t) return;

        // option1 is now Pack Size
        const isOption1 = t.matches(`input[name="option1"][form="${formId}"]`) ||
                          t.matches(`select[name="option1"][form="${formId}"]`) ||
                          t.matches(`input[name="option1"]`);
        
        // option2 is now Size
        const isOption2 = t.matches(`input[name="option2"][form="${formId}"]`) ||
                          t.matches(`select[name="option2"][form="${formId}"]`) ||
                          t.matches(`input[name="option2"]`);

        // Pack size changed (option1)
        if (isOption1) {
          console.log('[bundle-builder] Pack size (option1) changed:', t.value);
          handlePackSizeChange(root, formId);
        }

        // Size changed (option2)
        if (isOption2) {
          console.log('[bundle-builder] Size (option2) changed:', t.value);
          refreshSoldOut(root);
          updateAllSlotLabels(root);
        }
      });

      // Also listen for the theme's variant:change event
      document.addEventListener('variant:change', (e) => {
        console.log('[bundle-builder] variant:change event:', e.detail);
        
        const variant = e.detail?.variant;
        if (variant) {
          const packSize = getPackSizeFromVariant(variant);
          console.log('[bundle-builder] Pack size from variant:', packSize);
          if (packSize > 0) {
            applyRequiredSlots(root, packSize, { resetSelections: true });
          }
        }
        refreshSoldOut(root);
      });

      // Listen for input events on hidden inputs (for dropdown/popover changes)
      // The theme may update hidden inputs without triggering change
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            const target = mutation.target;
            // option1 is now Pack Size
            if (target.matches && target.matches('input[name="option1"]')) {
              console.log('[bundle-builder] option1 (pack size) value attribute changed:', target.value);
              handlePackSizeChange(root, formId);
            }
          }
        }
      });

      // Observe hidden inputs for option1 (pack size)
      $$('input[name="option1"][type="hidden"]').forEach(input => {
        observer.observe(input, { attributes: true, attributeFilter: ['value'] });
      });

      // Initial pack size application
      const initialPackSize = getSelectedPackSize(formId);
      console.log('[bundle-builder] Initial pack size:', initialPackSize);
      if (initialPackSize > 0) {
        applyRequiredSlots(root, initialPackSize);
      }
    }

    refreshSoldOut(root, { skipScroll: true });
    updateAllSlotLabels(root);
    updateBundleGallery(root, null, { skipScroll: true });
  }

  document.addEventListener('DOMContentLoaded', () => {
    const root = getRoot();
    if (!root) return;
    bind(root);
  });
})();